import { ApiModel, PointUtilities } from '@ng-cloud/badger-core';
import { Point } from '@ng-cloud/badger-core/models/interfaces/point';
import { Zone } from '@ng-cloud/badger-core/models/interfaces/zone';
import { FabricObject, FabricText, Group, Point as FabricPoint, Polygon } from 'fabric';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { NavZone } from '../../models/nav-zone';
import { ScanZone } from '../../models/scan-zone';
import { StoreZone } from '../../models/store-zone';
import { MapObject } from './map-object';

export class ZoneObject extends MapObject {
  static readonly minSize = 12.5;

  defaultOptions: any = {
    opacity: 1.0,
    selectable: false,
    evented: false,
    lockScalingFlip: true,
    cornerSize: 16,
    cornerColor: 'rgb(10 141 255)',
    borderColor: 'rgb(10 141 255)',
    borderScaleFactor: 2.5,
    padding: 4,
    perPixelTargetFind: true,
    polygonOptions: {
      fill: 'blue',
      opacity: 0.20,
      strokeWidth: 1,
      stroke: '#000000',
      perPixelTargetFind: true
    },
    labelOptions: {
      fontSize: 12,
      fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
      originX: 'center',
      originY: 'center',
      textAlign: 'center',
      textBackgroundColor: '#484b4fAA',
      fill: 'white',
      perPixelTargetFind: true
    },
    zoneVisible: true,
    labelVisible: true
  };

  private polygon: Polygon;
  private zoneGroup: Group;
  private zoneLabel: FabricText;
  private minScaleX: number;
  private minScaleY: number;

  private zoneSource: Subject<ZoneObject> = new Subject<ZoneObject>();
  zoneChanged: Observable<ZoneObject> = this.zoneSource.asObservable();

  constructor(public zone: ApiModel & Zone, private points: Point[], config?: any) {
    super(config);
  }

  destroy() {
    this.zoneSource.complete();
    super.destroy();
  }

  // ---------- label helpers ----------
  private formatSpeed(n?: number): string {
    if (typeof n !== 'number' || isNaN(n)) {
      return '';
    }
    return Number(n).toFixed(1).replace(/\.0$/, '');
  }

  private navZoneLabel(nz: NavZone): string {
    if (nz.description?.trim()) {
      return nz.description.trim();
    }

    switch (nz.mode) {
      case NavZone.KEEP_IN_MODE:
        return 'Keep In';
      case NavZone.KEEP_OUT_MODE:
        return 'Keep Out';
      case NavZone.SPEED_MODE: {
        const v = this.formatSpeed(nz.speed_mps);
        return v ? `${v} m/s\nPriority: ${nz.priority}` : 'Speed Zone';
      }
      default:
        return _.startCase(nz.mode);
    }
  }

  public applySpeedBaseline(baseline: number) {
    (this.getOptions() as any).speedBaseline = baseline;
    this.render();
  }

  protected build(): FabricObject {
    const options = this.getOptions();

    // Base polygon
    this.polygon = new Polygon(this.points, options.polygonOptions);
    this.polygon.set({ data: this.zone });

    // Label text
    let description: string;
    if (this.zone instanceof NavZone) {
      description = this.navZoneLabel(this.zone as NavZone);
    }
    else if (this.zone instanceof StoreZone) {
      description = this.zone.zoneTemplate ? this.zone.zoneTemplate.zoneName.description : '';
    }
    else if (this.zone instanceof ScanZone) {
      description = this.zone.name ? this.zone.name : '';
    }
    else {
      description = _.get(this.zone, 'description', '');
    }

    const padded = description ? ` ${description} ` : '';

    this.zoneLabel = new FabricText(padded, options.labelOptions);
    const labelCenter = PointUtilities.getLabelCenter(this.points, 1);
    this.zoneLabel.left = labelCenter.x;
    this.zoneLabel.top = labelCenter.y;

    // Group
    const zoneObjects: FabricObject[] = [];
    if (options.zoneVisible) {
      zoneObjects.push(this.polygon);
    }
    if (options.labelVisible) {
      zoneObjects.push(this.zoneLabel);
    }

    this.zoneGroup = new Group(zoneObjects, options);

    // Min scale enforcement
    this.minScaleX = ZoneObject.minSize / this.zoneGroup.width;
    this.minScaleY = ZoneObject.minSize / this.zoneGroup.height;

    return this.zoneGroup;
  }

  protected setup(): void {
    this.getFabric().on('moving', () => this.notifyChange());
    this.getFabric().on('rotating', () => this.notifyChange());

    this.getFabric().on('scaling', () => {
      this.setFill();
      this.notifyChange();
    });

    this.getFabric().on('modified', () => {
      this.zoneGroup.set({
        scaleX: Math.max(this.zoneGroup.scaleX, this.minScaleX),
        scaleY: Math.max(this.zoneGroup.scaleY, this.minScaleY)
      });
      this.zoneGroup.setCoords();

      // keep polygon fill in sync with options
      this.polygon.set('fill', this.getOptions().polygonOptions.fill);

      this.render();
      this.notifyChange();
    });

    this.zoneChanged.subscribe(() => {
      const labelScaleX = this.zoneGroup.width / (this.zoneGroup.width * this.zoneGroup.scaleX);
      const labelScaleY = this.zoneGroup.height / (this.zoneGroup.height * this.zoneGroup.scaleY);
      this.zoneLabel.set({ angle: -this.zoneGroup.angle, scaleX: labelScaleX, scaleY: labelScaleY });
    });
  }

  protected notifyChange() {
    this.zoneSource.next(this);
  }

  getPoints(): Point[] {
    const matrix = this.polygon.calcTransformMatrix();
    return this.polygon.points
      .map((pt) => new FabricPoint(pt.x - this.polygon.pathOffset.x, pt.y - this.polygon.pathOffset.y))
      .map((pt) => pt.transform(matrix));
  }

  protected setFill() {
    const color = this.isValid() ? this.getOptions().polygonOptions.fill : 'gray';
    if (color !== this.polygon.fill) {
      this.polygon.set('fill', color);
      this.render();
    }
  }

  private isValid() {
    return this.zoneGroup.scaleX >= this.minScaleX && this.zoneGroup.scaleY >= this.minScaleY;
  }

  static isValidZone(width: number, height: number): boolean {
    return width >= ZoneObject.minSize && height >= ZoneObject.minSize;
  }

  getBaseShape(): FabricObject {
    const group = this.getFabric() as Group;
    return group.getObjects()[0] as FabricObject; // polygon
  }

  // -------- overlap clipping (group-level) --------
  cloneShapeForClip(clonedBase: (clone: FabricObject) => void) {
    const abs = this.getPoints();

    const minX = Math.min(...abs.map(p => p.x));
    const minY = Math.min(...abs.map(p => p.y));
    const rel = abs.map(p => ({ x: p.x - minX, y: p.y - minY }));

    const clip = new Polygon(rel, {
      left: minX,
      top: minY,
      originX: 'left',
      originY: 'top',
      fill: '#000',
      strokeWidth: 0,
      selectable: false,
      evented: false,
      objectCaching: false
    });

    clip.setCoords();
    clonedBase(clip);
  }

  applyOverlapClipFrom(hiders: FabricObject[]) {
    const group = this.getFabric();
    if (!hiders.length) {
      group.clipPath = undefined;
      group.dirty = true;
      group.canvas?.requestRenderAll();
      return;
    }

    group.clipPath = new Group(hiders, {
      inverted: true as any,
      absolutePositioned: true as any
    });

    group.dirty = true;
    group.canvas?.requestRenderAll();
  }
}
